import type {IAdminMainStore} from "@app/types";
import type {ICalendarData, IManualAppointment} from "@torim-app/types";
import {AppointmentStatus, AppointmentType} from "@torim-app/types";
import {DateTime} from "luxon";
import {defineStore} from 'pinia'

import {AppointmentsApi} from "@app/api/admin/appointments-api";
import {useAdminStore} from "@app/stores/admin";

export const useAdminHomeStore = defineStore('admin-home', {
  state: (): IAdminMainStore => {
    const adminStore = useAdminStore();
    return {
      calendarData: <ICalendarData>{},
      selectedDateAtCalendar: new Date(),
      isLoading: false,
      selectedProviderId: adminStore.userId,
      lastFetchedFrom: undefined,
      lastFetchedTo: undefined,
      lastFetchedProviderIds: undefined,
    }
  },
  getters: {
    getSelectedDateAtCalendar(state): Date {
      return state.selectedDateAtCalendar;
    },
    getCalendarData(state): ICalendarData {
      return state.calendarData;
    },
    getSelectedProviderId(state): string {
      return state.selectedProviderId;
    },
    getCalendarDataByDate(state) {
      return (dateFormatted: string) => {
        return state.calendarData.events.filter(x => x.start.includes(dateFormatted));
      };
    },
    getCalendarDataByProviders(state) {
      return (providerIds: string[]) => {
        if (!providerIds || providerIds.length === 0) {
          return state.calendarData;
        }
        return {
          ...state.calendarData,
          events: state.calendarData.events.filter(event =>
            event.providerId ? providerIds.includes(event.providerId) : true
          )
        };
      };
    }
  },
  actions: {
    setProviderId(providerId: string) {
      this.selectedProviderId = providerId;
    },
    async fetchBusinessCalendarData(fromDate?: string, toDate?: string): Promise<void> {
      this.isLoading = true;
      if (fromDate) this.lastFetchedFrom = fromDate;
      if (toDate) this.lastFetchedTo = toDate;
      this.lastFetchedProviderIds = [this.selectedProviderId];
      const calendarData = await AppointmentsApi.fetchCalendarData(this.selectedProviderId, fromDate, toDate);
      calendarData.events = calendarData.events.map(event => ({
        ...event,
        providerId: event.providerId || this.selectedProviderId,
        deletable: false,
        resizable: false,
        class: event.type === AppointmentType.OUT_OF_OFFICE ? 'blocked-hour' : event.status === AppointmentStatus.NO_SHOW ? 'no-show-appointment' : (event.status === AppointmentStatus.VERIFIED ? 'appointment' : 'appointment-approved'),
        background: event.type === AppointmentType.OUT_OF_OFFICE,
      }));
      this.calendarData = calendarData;
      this.isLoading = false;
    },
    async fetchBusinessCalendarDataSilently(): Promise<void> {
      // Resolve the providers we last loaded so silent refreshes don't
      // narrow a multi-provider view back down to the single selected one.
      const providerIds = this.lastFetchedProviderIds && this.lastFetchedProviderIds.length > 0
        ? this.lastFetchedProviderIds
        : [this.selectedProviderId];

      const fetchIds = providerIds.length > 1 ? providerIds : providerIds[0];
      const calendarData = await AppointmentsApi.fetchCalendarData(fetchIds, this.lastFetchedFrom, this.lastFetchedTo);
      calendarData.events = calendarData.events.map(event => ({
        ...event,
        providerId: event.providerId || (typeof fetchIds === 'string' ? fetchIds : undefined),
        deletable: false,
        resizable: false,
        class: event.type === AppointmentType.OUT_OF_OFFICE ? 'blocked-hour' : event.status === AppointmentStatus.NO_SHOW ? 'no-show-appointment' : (event.status === AppointmentStatus.VERIFIED ? 'appointment' : 'appointment-approved'),
        background: event.type === AppointmentType.OUT_OF_OFFICE,
      }));
      this.calendarData = calendarData;
    },
    async fetchBusinessCalendarDataForProviders(providerIds: string[], fromDate?: string, toDate?: string): Promise<void> {
      this.isLoading = true;
      if (fromDate) this.lastFetchedFrom = fromDate;
      if (toDate) this.lastFetchedTo = toDate;
      this.lastFetchedProviderIds = [...providerIds];
      const calendarData = await AppointmentsApi.fetchCalendarData(providerIds, fromDate, toDate);
      calendarData.events = calendarData.events.map(event => ({
        ...event,
        deletable: false,
        resizable: false,
        class: event.type === AppointmentType.OUT_OF_OFFICE ? 'blocked-hour' : event.status === AppointmentStatus.NO_SHOW ? 'no-show-appointment' : (event.status === AppointmentStatus.VERIFIED ? 'appointment' : 'appointment-approved'),
        background: event.type === AppointmentType.OUT_OF_OFFICE,
      }));
      this.calendarData = calendarData;
      this.isLoading = false;
    },
    async addManualEventToCalendar(appointment: IManualAppointment): Promise<void> {
      this.isLoading = true;
      await AppointmentsApi.addManualAppointment({...appointment, providerId: appointment.providerId || this.getSelectedProviderId})
      await this.fetchBusinessCalendarDataSilently();
      this.isLoading = false;
    },
    setSelectedDateAtCalendar(date: DateTime, disable: boolean) {
      if (!disable)
        this.selectedDateAtCalendar = date.toJSDate();
    },
  }
})
